/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnDestroy, type OnInit, Component, EventEmitter, Input, Output } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { type License, EventService, ExtendService, I18nService, PermissionService } from '@dm/core';
import { LicenseKey } from 'app/common/constants/license-key';
import { FeatureConfig } from 'app/config/feature';
import { type Observable, type Subscription, of } from 'rxjs';

interface TaskMenuItem {
    label?: string;
    command?: (TaskMenuItem) => void;
    permission?: string[] | string[][];
    license?: string[] | string[][];
    url?: string;
    disabled?: boolean;
}

@Component({
    selector: 'app-header-common-task',
    templateUrl: './header-common-task.component.html',
    styleUrls: ['./header-common-task.component.scss'],
})
export class HeaderCommonTaskComponent implements OnInit, OnDestroy {
    @Input() isSmallSize = false;
    @Output() destroy: EventEmitter<any> = new EventEmitter();
    @Output() onHidePanel = new EventEmitter();

    resourceItems: TaskMenuItem[] = [];
    protectionItems: TaskMenuItem[] = [];
    tooltipVisible = false;

    i18nStr = {
        dataProtection: this.i18n.get('common.dataProtection_title'),
        service_title: this.i18n.get('common.service_title'),
    };

    private subs$: Subscription[] = [];

    constructor(
        private i18n: I18nService,
        private permissionService: PermissionService,
        private router: Router,
        private ext: ExtendService,
        private eventService: EventService,
    ) {}

    ngOnInit(): void {
        this.initItems();

        this.subs$.push(
            this.eventService.subscribe('license.update', () => {
                this.initItems();
            }),
            this.router.events.subscribe((evt) => {
                if (evt instanceof NavigationEnd) {
                    if (this.tooltipVisible) {
                        this.tooltipVisible = false;
                        this.onHidePanel.emit();
                    }
                }
            }),
        );
    }

    initItems(): void {
        this.initResourceItems();
        this.initProtectionItems();
    }

    /**
     * 初始化服务item
     */
    initResourceItems(): void {
        this.resourceItems = [
            {
                label: this.i18n.get('home.commonTask_term_createLunGroup_button'),
                command: this.createLunGroup,
                url: `/service/block/${FeatureConfig.IsReplaceStrLunWithNamespace ?
                    'namespace-group' :
                    'lun-group'}?action=create&tab=${this.i18n.get('common.lunGroup_title')}`,
                permission: ['lun_group_W'],
                license: FeatureConfig.IsReplaceStrLunWithNamespace ? [] : [LicenseKey.SmartThin],
            },
            {
                label: this.i18n.get('home.commonTask_term_createHost_button'),
                command: this.createHost,
                url: `/service/block/hosts?action=createHost&tab=${this.i18n.get('common.hosts_title')}`,
                permission: ['host_W'],
                license: [LicenseKey.SmartThin],
            },
            {
                label: this.i18n.get('service.fileSystem_term_create_title'),
                command: this.createFileSystem,
                url: `/service/file/file-system?action=create&tab=${this.i18n.get('common.fileSystem_title')}`,
                license: [LicenseKey.NAS],
            },
            ...!FeatureConfig.IsShowMenuItemsForOceanProtect ? [] : [
                {
                    label: this.i18n.get('service.dtree_term_create_title'),
                    command: this.createDtree,
                    url: `/service/file/dtree?action=create&tab=${this.i18n.get('common.dtree_lab')}`,
                    license: [LicenseKey.NAS],
                },
            ],
            {
                label: this.i18n.get('service.share_term_createNFS_title'),
                command: this.createNfsShare,
                url: `/service/file/share?action=create&tab=${this.i18n.get('common.nfsShare_lab')}`,
                license: [LicenseKey.NAS],
            },
        ].filter((item) => this.check(item));
    }

    /**
     * 初始化保护item
     */
    initProtectionItems(): void {
        this.protectionItems = [
            {
                label: this.i18n.get('home.commonTask_term_createPG_button'),
                command: this.createPG,
                url: `/protection/object/protect-group?action=create&tab=${this.i18n.get('protection.protectionGroup_title')}`,
                permission: ['protect_group_W'],
                license: [LicenseKey.SmartThin],
            },
            {
                label: this.i18n.get('home.commonTask_term_createSnapshotCG_button'),
                command: this.createSnapshotCG,
                permission: ['snap_group_W'],
                url: `/protection/object/protect-group?action=create&tab=${this.i18n.get('protection.snapshotCGs_title')}`,
                license: [[LicenseKey.HyperSnap, LicenseKey.HyperClone, LicenseKey.HyperCDP], [LicenseKey.SmartThin]],
            },
        ].filter((item) => this.check(item));
        if (!this.resourceItems.length && !this.protectionItems.length) {
            this.destroy.emit();
        }
    }

    // 检查权限
    check = (item: TaskMenuItem): boolean => {
        const show = this.permissionService.checkShow(item.permission, item.license);
        return show;
    };

    // 如果就在当前页面点击，采用f5刷新的方式
    checkRoute(item: TaskMenuItem): boolean {
        const currUrl = this.router.url;
        const futureUrl = item.url.split('?')[0];
        if (currUrl.startsWith(futureUrl)) {
            window.location.href = item.url;
            this.tooltipVisible = false;
            this.onHidePanel.emit();
            return false;
        }
        return true;
    }

    createLunGroup = (item: TaskMenuItem): void => {
        this.permissionService.checkDepPermission([['lun_R', 'lun_W'], ['task_W']])
            .subscribe(() => {
                if (!this.checkRoute(item)) {
                    return;
                }
                this.router.navigate([`/service/block/${FeatureConfig.IsReplaceStrLunWithNamespace ? 'namespace-group' : 'lun-group'}`], {
                    queryParams: {
                        action: 'create',
                        tab: this.i18n.get('common.lunGroup_title'),
                    },
                    state: null,
                });
                this.tooltipVisible = false;
                this.onHidePanel.emit();
            });
    };

    createHost = (item: TaskMenuItem): void => {
        if (!this.checkRoute(item)) {
            return;
        }
        this.router.navigate(['/service/block/hosts'], {
            queryParams: {
                tab: this.i18n.get('common.hosts_title'),
                action: 'createHost',
            },
        });
        this.tooltipVisible = false;
        this.onHidePanel.emit();
    };

    createFileSystem = (item: TaskMenuItem): void => {
        if (!this.checkRoute(item)) {
            return;
        }
        this.router.navigate(['/service/file/file-system'], {
            queryParams: {
                action: 'create',
                tab: this.i18n.get('common.fileSystem_title'),
            },
        });
    };

    createDtree = (item: TaskMenuItem): void => {
        if (!this.checkRoute(item)) {
            return;
        }
        this.router.navigate(['/service/file/dtree'], {
            queryParams: {
                action: 'create',
                tab: this.i18n.get('common.dtree_lab'),
            },
        });
    };

    createNfsShare = (item: TaskMenuItem): void => {
        if (!this.checkRoute(item)) {
            return;
        }
        this.router.navigate(['/service/file/share'], {
            queryParams: {
                action: 'create',
                tab: this.i18n.get('common.nfsShare_lab'),
            },
        });
    };

    createPG = (item: TaskMenuItem): void => {
        this.permissionService.checkDepPermission([['lun_R', 'lun_group_R'], ['task_W']])
            .subscribe(() => {
                const cgUrl = FeatureConfig.IsShowMenuItemsForOceanProtect ? '/protection/protection-group' :
                    '/protection/object/protect-group';

                if (!this.checkRoute(item)) {
                    return;
                }
                this.router.navigate([cgUrl], {
                    queryParams: {
                        tab: this.i18n.get('protection.protectionGroup_title'),
                        action: 'create',
                    },
                });
                this.tooltipVisible = false;
                this.onHidePanel.emit();
            });
    };

    createSnapshotCG = (item: TaskMenuItem): void => {
        this.ext.forkOrder([
            this.checkLicense([[LicenseKey.HyperSnap, LicenseKey.HyperClone, LicenseKey.HyperCDP]]),
            this.permissionService.checkDepPermission(['protect_group_R']),
        ]).subscribe(() => {
            const snapUrl = FeatureConfig.IsShowMenuItemsForOceanProtect ? '/protection/snapshot' : '/protection/object/protect-group';
            const snapTab = FeatureConfig.IsShowMenuItemsForOceanProtect ?
                this.i18n.get('protection.protectionGroup_title') :
                this.i18n.get('protection.snapshotCGs_title');

            if (!this.checkRoute(item)) {
                return;
            }
            this.router.navigate([snapUrl], {
                queryParams: {
                    tab: snapTab,
                    action: 'create',
                },
            });
            this.tooltipVisible = false;
            this.onHidePanel.emit();
        });
    };

    ngOnDestroy(): void {
        for (const sub$ of this.subs$) {
            sub$.unsubscribe();
        }
    }

    private checkLicense(licenses): Observable<License> {
        if (!licenses || !licenses.length) {
            return of(null);
        }
        return this.ext.forkOrder((licenses as any[])
            .map((lic) => this.permissionService.checkLicense(lic)));
    }
}
